// The list of roles admins assign to users, kept at users/roles in the
// Realtime Database as id → name. Names are matched exactly by the app's
// route guards and stored as keys under users/user_roles/{role}.

// Roles the app itself depends on, so never removed from the list: ADMIN
// opens the admin pages, and GUEST is what a user without a role gets.
export const PROTECTED_ROLES = ["ADMIN", "GUEST"];

// A role name as the list keeps it: trimmed, single-spaced and upper case,
// like the roles already on it.
export function cleanRoleName(text) {
  return String(text || "")
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

const sameRole = (a, b) =>
  String(a || "").toUpperCase() === String(b || "").toUpperCase();

// The names on the list, in stored order, skipping gaps.
export function roleNames(list) {
  return Object.values(list || {}).filter(
    (name) => typeof name === "string" && name
  );
}

// What's wrong with `name` (already cleaned) as a new role, or "" when it
// can go on the list. The database can't store . # $ [ ] or / in a key.
export function roleProblem(name, existing = []) {
  if (!name) return "Enter a role name.";
  if (/[.#$[\]/]/.test(name)) return "Role names can't contain . # $ [ ] or /.";
  if (name.length > 40) return "Keep it to 40 characters or fewer.";
  if (existing.some((r) => sameRole(r, name))) {
    return `${name} is already a role.`;
  }
  return "";
}

const entriesOf = (list) =>
  Object.entries(list || {}).filter(
    ([, name]) => typeof name === "string" && name
  );

const numbered = (entries) => entries.every(([key]) => /^\d+$/.test(key));

// The stored list with `name` added, kept the way it's stored: a list with
// numbered keys (read back as an array) gets the next number, and one with
// generated keys gets `newKey()`. Undefined when the name is already there,
// which leaves the list as it is.
export function withRole(list, name, newKey) {
  const entries = entriesOf(list);
  if (entries.some(([, existing]) => sameRole(existing, name))) {
    return undefined;
  }
  const key = numbered(entries)
    ? String(entries.reduce((next, [k]) => Math.max(next, Number(k) + 1), 0))
    : newKey();
  return { ...Object.fromEntries(entries), [key]: name };
}

// The stored list without `name`. A numbered list closes up, so readers
// that take it as an array see no gaps. Undefined when the name isn't
// there.
export function withoutRole(list, name) {
  const entries = entriesOf(list);
  const kept = entries.filter(([, existing]) => existing !== name);
  if (kept.length === entries.length) return undefined;
  return numbered(entries)
    ? kept.map(([, existing]) => existing)
    : Object.fromEntries(kept);
}

// A user's roles as an array, whether stored as one name or several.
export const userRoles = (user) =>
  Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : [];

// How many users hold each role, by exact name.
export function roleCounts(users) {
  const counts = {};
  users.forEach((user) => {
    userRoles(user).forEach((role) => {
      counts[role] = (counts[role] || 0) + 1;
    });
  });
  return counts;
}
